import { useCallback, useEffect, useState } from "react";

import type { Package, PackageLink } from "@aos/shared";
import { api } from "@/lib/api";

const PAGE_SIZE = 200;

interface ListEnvelope<T> {
  readonly items: readonly T[];
  readonly total: number;
}

interface PageRequest {
  readonly path: string;
  readonly sort: string;
  readonly order: "asc" | "desc";
  readonly signal: AbortSignal;
}

export interface PackageGraphData {
  readonly packages: readonly Package[];
  readonly links: readonly PackageLink[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: Error | null;
  readonly refetch: () => void;
}

async function fetchAllPages<T>({
  path,
  sort,
  order,
  signal,
}: PageRequest): Promise<readonly T[]> {
  const items: T[] = [];
  let page = 1;

  while (true) {
    const envelope = await api.get<ListEnvelope<T>>(path, {
      query: { page, pageSize: PAGE_SIZE, sort, order },
      signal,
    });

    items.push(...envelope.items);

    if (items.length >= envelope.total || envelope.items.length === 0) {
      return items;
    }

    page += 1;
  }
}

export function usePackageGraphData(): PackageGraphData {
  const [refreshKey, setRefreshKey] = useState(0);
  const [packages, setPackages] = useState<readonly Package[]>([]);
  const [links, setLinks] = useState<readonly PackageLink[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    setIsLoading(true);
    setError(null);

    void Promise.all([
      fetchAllPages<Package>({
        path: "/packages",
        sort: "package_type",
        order: "asc",
        signal: controller.signal,
      }),
      fetchAllPages<PackageLink>({
        path: "/package-links",
        sort: "created_at",
        order: "asc",
        signal: controller.signal,
      }),
    ])
      .then(([nextPackages, nextLinks]) => {
        setPackages(nextPackages);
        setLinks(nextLinks);
        setIsLoading(false);
      })
      .catch((nextError: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          nextError instanceof Error
            ? nextError
            : new Error("Failed to load package graph data."),
        );
        setIsLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [refreshKey]);

  const refetch = useCallback(() => {
    setRefreshKey((current) => current + 1);
  }, []);

  return {
    packages,
    links,
    isLoading,
    isError: error !== null,
    error,
    refetch,
  };
}
